import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '@/lib/utils'

import { Markdown } from '@/components/ui/markdown'

const alertVariants = cva(
  "group/alert relative grid w-full gap-0.5 rounded-lg px-2.5 py-2 text-left text-sm has-data-[slot=alert-action]:relative has-data-[slot=alert-action]:pr-18 has-[>svg]:grid-cols-[auto_1fr] has-[>svg]:gap-x-2 *:[svg]:row-span-2 *:[svg]:translate-y-0.5 *:[svg]:text-current *:[svg:not([class*='size-'])]:size-4",
  {
    variants: {
      variant: {
        default:
          'border border-border bg-card text-card-foreground data-[slot=alert-description]:text-card-foreground/90 dark:bg-muted',
        success:
          'bg-success/10 text-success *:data-[slot=alert-description]:text-success/90 *:[svg]:text-success',
        error:
          'bg-error/10 text-error *:data-[slot=alert-description]:text-error/90 *:[svg]:text-error',
        warning:
          'bg-warning/10 text-warning *:data-[slot=alert-description]:text-warning/90 *:[svg]:text-warning',
        info: 'bg-info/10 text-info *:data-[slot=alert-description]:text-info/90 *:[svg]:text-info',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
)

type AlertVariants = VariantProps<typeof alertVariants>

function Alert({ className, variant, ...props }: React.ComponentProps<'div'> & AlertVariants) {
  return (
    <div
      data-slot="alert"
      role="alert"
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  )
}

function AlertTitle({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-title"
      className={cn(
        'font-medium group-has-[>svg]/alert:col-start-2 [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground',
        className
      )}
      {...props}
    />
  )
}

/** Se o texto for uma string, aceita markdown simples (negrito, listas, links). JSX passa como está. */
function AlertDescription({ className, children, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="alert-description"
      className={cn(
        'text-sm text-balance text-muted-foreground md:text-pretty [&_a]:underline [&_a]:underline-offset-3 [&_a]:hover:text-foreground [&_p:not(:last-child)]:mb-4',
        className
      )}
      {...props}
    >
      {typeof children === 'string' ? (
        <Markdown className="gap-1 text-[length:inherit]">{children}</Markdown>
      ) : (
        children
      )}
    </div>
  )
}

function AlertAction({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div data-slot="alert-action" className={cn('absolute top-2 right-2', className)} {...props} />
  )
}

export { Alert, AlertAction, AlertDescription, AlertTitle }
